import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import AtlasMap from "@/pages/AtlasMap";
import Discipline from "@/pages/Discipline";
import Workshop from "@/pages/Workshop";
import Horizon from "@/pages/Horizon";
import Quests from "@/pages/Quests";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import MyInterests from "@/pages/MyInterests";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PublicProfile from "@/pages/PublicProfile";
import Inbox from "@/pages/Inbox";
import Matchmaker from "@/pages/Matchmaker";
import ProjectsBrowse from "@/pages/ProjectsBrowse";
import ProjectsNew from "@/pages/ProjectsNew";
import ProjectDetail from "@/pages/ProjectDetail";
import BountiesBrowse from "@/pages/BountiesBrowse";
import BountyDetail from "@/pages/BountyDetail";
import Careers from "@/pages/Careers";
import JobsNew from "@/pages/JobsNew";
import JobDetail from "@/pages/JobDetail";
import AdminEmails from "@/pages/AdminEmails";
import AdminLookup from "@/pages/AdminLookup";
import AdminLeads from "@/pages/AdminLeads";
import AdminVerifications from "@/pages/AdminVerifications";
import About from "@/pages/About";
import Resume from "@/pages/Resume";
import ResumeEditor from "@/pages/ResumeEditor";
import VerifyVet from "@/pages/VerifyVet";
import CookieCheckBanner from "@/components/CookieCheckBanner";

function ProtectedRoute({ children, requireOnboarded = true, requirePaid = true }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">
                Loading Atlas…
            </div>
        );
    }
    if (!user) return <Navigate to="/" replace state={{ from: location }} />;
    if (requireOnboarded && !user.onboarded && location.pathname !== "/onboarding")
        return <Navigate to="/onboarding" replace />;
    if (requirePaid && user.trial_used && !user.has_access && location.pathname !== "/paywall")
        return <Navigate to="/paywall" replace />;
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/@:username" element={<PublicProfile />} />
            <Route path="/u/:username" element={<PublicProfile />} />
            <Route path="/u/:username/resume" element={<Resume />} />
            <Route path="/@:username/resume" element={<Resume />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route
                path="/paywall"
                element={
                    <ProtectedRoute requireOnboarded={false} requirePaid={false}>
                        <Paywall />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute requireOnboarded={false} requirePaid={false}>
                        <Onboarding />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/atlas"
                element={
                    <ProtectedRoute>
                        <AtlasMap />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/discipline/:slug"
                element={<Discipline />}
            />
            <Route
                path="/workshop/:slug"
                element={
                    <ProtectedRoute>
                        <Workshop />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/horizon/:slug"
                element={
                    <ProtectedRoute>
                        <Horizon />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/quests"
                element={
                    <ProtectedRoute>
                        <Quests />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/interests"
                element={
                    <ProtectedRoute>
                        <MyInterests />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/inbox"
                element={
                    <ProtectedRoute>
                        <Inbox />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/matchmaker"
                element={
                    <ProtectedRoute>
                        <Matchmaker />
                    </ProtectedRoute>
                }
            />
            <Route path="/projects" element={<ProjectsBrowse />} />
            <Route
                path="/projects/new"
                element={
                    <ProtectedRoute>
                        <ProjectsNew />
                    </ProtectedRoute>
                }
            />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/bounties" element={<BountiesBrowse />} />
            <Route path="/bounties/:id" element={<BountyDetail />} />
            <Route path="/careers" element={<Careers />} />
            <Route
                path="/jobs/new"
                element={
                    <ProtectedRoute>
                        <JobsNew />
                    </ProtectedRoute>
                }
            />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route
                path="/admin/emails"
                element={
                    <ProtectedRoute>
                        <AdminEmails />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/lookup"
                element={
                    <ProtectedRoute>
                        <AdminLookup />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/leads"
                element={
                    <ProtectedRoute>
                        <AdminLeads />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/resume"
                element={
                    <ProtectedRoute>
                        <ResumeEditor />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/verify-vet"
                element={
                    <ProtectedRoute requireOnboarded={false} requirePaid={false}>
                        <VerifyVet />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/verifications"
                element={
                    <ProtectedRoute requireOnboarded={false} requirePaid={false}>
                        <AdminVerifications />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    // Global online/offline listeners — show a subtle toast when the browser
    // loses/regains connectivity so users never blame Atlas for their WiFi.
    React.useEffect(() => {
        const goOffline = () => {
            toast.error("You're offline — some pages won't load until you're back on.", {
                id: "atlas-offline",
                duration: Infinity,
            });
        };
        const goOnline = () => {
            toast.dismiss("atlas-offline");
            toast.success("Back online.", { duration: 2000 });
        };
        window.addEventListener("offline", goOffline);
        window.addEventListener("online", goOnline);
        return () => {
            window.removeEventListener("offline", goOffline);
            window.removeEventListener("online", goOnline);
        };
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CookieCheckBanner />
                    <AppRoutes />
                    <Toaster />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
